"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { Subscription, SubscriptionStatus } from "@/types";
import { subscriptionService } from "@/services/subscriptionService";
import { userSubscriptionService } from "@/services/userSubscriptionService";
import { useCustomers } from "./CustomerContext";
import { useActivities } from "./ActivityContext";
import { usePackages } from "./PackageContext";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";
import {
  UserSubscriptionInput,
  UserSubscriptionModelOutput,
  UserSubscriptionUpdateInput,
  SubscriptionFeature,
} from "@/types/userSubscription.types";
import { AddSubscriptionFormData } from "@/components/subscription/AddSubscriptionDialog";
import { toBackendDiscount } from "@/lib/utils";

interface SubscriptionContextType {
  subscriptions: Subscription[];
  loading: boolean;
  error: string | null;
  addSubscription: (subscription: Omit<Subscription, "id">) => Subscription;
  createUserSubscription: (
    formData: AddSubscriptionFormData,
    planFeatures?: SubscriptionFeature[],
  ) => Promise<Subscription>;
  updateSubscription: (id: string, subscription: Partial<Subscription>) => void;
  updateUserSubscriptionDetails: (
    id: string,
    updates: { day?: string; time?: string; vehicleId?: string },
  ) => Promise<void>;
  updateUserSubscriptionIsActive: (
    id: string,
    isActive: boolean,
  ) => Promise<UserSubscriptionModelOutput>;
  deleteSubscription: (id: string) => void;
  getSubscriptionById: (id: string) => Subscription | undefined;
  getActiveSubscriptionByCustomerId: (
    customerId: string,
  ) => Subscription | undefined;
  getActiveSubscriptionsByCustomerId: (customerId: string) => Subscription[];
  getSubscriptionsByCustomerId: (customerId: string) => Subscription[];
  hasActivePlatinumSubscription: (customerId: string) => boolean;
  fetchSubscriptions: () => Promise<void>;
  refreshSubscriptions: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined,
);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { customers } = useCustomers();
  const { addActivity } = useActivities();
  const { subscriptionPlans } = usePackages();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Transform UserSubscriptionModelOutput to frontend Subscription format
  const transformUserSubscription = useCallback(
    (userSub: UserSubscriptionModelOutput): Subscription => {
      // Use subscription plan from backend response, fallback to lookup
      const plan =
        userSub.subscription ||
        subscriptionPlans.find((p) => p.id === userSub.subscriptionId);
      const firstRule =
        userSub.rules && userSub.rules.length > 0 ? userSub.rules[0] : null;

      // Use vehicleId from backend (from vehicle object, vehicleId field, or extracted from rules)
      // Also check appointments for vehicleId if not found elsewhere
      let vehicleId =
        userSub.vehicle?.id || userSub.vehicleId || firstRule?.vehicleId || "";

      // If vehicleId is still empty, try to get it from appointments
      if (!vehicleId && userSub.appoinments && userSub.appoinments.length > 0) {
        // Subscription appointments carry a single vehicle in vehicleIds[0]
        const appointmentWithVehicle = userSub.appoinments.find(
          (apt) => apt.vehicleIds && apt.vehicleIds.length > 0,
        );
        if (appointmentWithVehicle?.vehicleIds?.[0]) {
          vehicleId = appointmentWithVehicle.vehicleIds[0];
        }
      }

      // Use status from backend if available, otherwise derive from isActive
      const status: SubscriptionStatus =
        (userSub.status as SubscriptionStatus) ||
        (userSub.isActive ? "active" : "expired");

      // Match plan features by normalized id or by name (stored ids are UUIDs)
      const findFeature = (key: string, namePart: string) =>
        userSub.features?.find(
          (f) =>
            f.normalizedId === key ||
            f.id === key ||
            f.name?.toLowerCase().includes(namePart),
        );
      const exteriorFeature = findFeature("exterior", "exterior cleaning");
      const interiorFeature = findFeature("interior", "interior cleaning");
      const dashPolishFeature = findFeature("dash_polish", "dash polish");

      // Times a feature was used, from the usage recorded on each appointment
      const countFeatureUsed = (feature?: SubscriptionFeature) => {
        if (!feature) return 0;
        return (
          userSub.appoinments?.filter((apt) =>
            apt.featuresUsed?.some((used) => used.featureId === feature.id),
          ).length || 0
        );
      };

      // The stored value of a counted feature is the remaining balance, so the
      // allowance is what is left plus what has been used. An included
      // (on/off) feature counts as 1.
      const getFeatureTotal = (feature: SubscriptionFeature | undefined, used: number) => {
        if (!feature) return 0;
        if (typeof feature.value === "boolean") return feature.value ? 1 : 0;
        return (Number(feature.value) || 0) + used;
      };

      const exteriorUsed = countFeatureUsed(exteriorFeature);
      const interiorUsed = countFeatureUsed(interiorFeature);
      const dashPolishUsed = countFeatureUsed(dashPolishFeature);

      const exteriorTotal = getFeatureTotal(exteriorFeature, exteriorUsed);
      const interiorTotal = getFeatureTotal(interiorFeature, interiorUsed);
      const dashPolishTotal = getFeatureTotal(dashPolishFeature, dashPolishUsed);

      // Calculate totals and remaining
      const totalServices = exteriorTotal + interiorTotal + dashPolishTotal;
      const remainingServices = Math.max(
        0,
        exteriorTotal -
          exteriorUsed +
          (interiorTotal - interiorUsed) +
          (dashPolishTotal - dashPolishUsed),
      );

      // Use statistics from backend if available, otherwise use calculated values
      const finalRemainingServices =
        userSub.statistics?.remainingServices ?? remainingServices;
      const finalTotalServices =
        userSub.statistics?.totalServices ?? totalServices;

      const earliestAppointmentDate =
        userSub.appoinments
          ?.map((apt) => new Date(apt.date))
          .filter((date) => !isNaN(date.getTime()))
          .sort((a, b) => a.getTime() - b.getTime())[0] || null;

      // Cycle start is the first wash. Older rows without it fall back to createdAt.
      const cycleStart = userSub.cycleStartDate
        ? new Date(userSub.cycleStartDate)
        : null;
      const cycleEndDate = userSub.cycleEndDate
        ? new Date(userSub.cycleEndDate)
        : null;

      let startDate: Date;
      if (cycleStart && !isNaN(cycleStart.getTime())) {
        startDate = cycleStart;
      } else if (userSub.createdAt) {
        const parsedDate = new Date(userSub.createdAt);
        // Check if date is valid
        if (isNaN(parsedDate.getTime())) {
          // If invalid, use earliest appointment date when available.
          console.warn(
            `Invalid createdAt date for subscription ${userSub.id}: ${userSub.createdAt}. Falling back to ${earliestAppointmentDate ? "earliest appointment date" : "current date"}.`,
          );
          startDate = earliestAppointmentDate || new Date();
        } else {
          startDate = parsedDate;
        }
      } else {
        // If createdAt is missing, use earliest appointment date when available.
        console.warn(
          `Missing createdAt for subscription ${userSub.id}. Falling back to ${earliestAppointmentDate ? "earliest appointment date" : "current date"}.`,
        );
        startDate = earliestAppointmentDate || new Date();
      }

      return {
        id: userSub.id,
        subscriptionNumber: userSub.subscriptionNumber ?? null,
        customerId: userSub.customerId,
        vehicleId,
        planId: userSub.subscriptionId,
        planName: userSub.subscription?.name || plan?.name || "Unknown Plan",
        monthlyPrice: userSub.subscription?.price || plan?.price || 0,
        startDate,
        cycleEndDate,
        recurringDay: firstRule?.day || "Monday",
        preferredTime: firstRule?.time || "10:00 AM",
        recurringDay2: userSub.rules?.[1]?.day,
        preferredTime2: userSub.rules?.[1]?.time,
        status,
        isActive: userSub.isActive,
        autoRenew: userSub.autoRenew ?? false,
        remainingServices: finalRemainingServices,
        totalServices: finalTotalServices,
        exteriorCleaningsUsed: exteriorUsed,
        exteriorCleaningsTotal: exteriorTotal,
        interiorCleaningsUsed: interiorUsed,
        interiorCleaningsTotal: interiorTotal,
        dashPolishUsed: dashPolishUsed,
        dashPolishTotal: dashPolishTotal,
        // Include populated customer and vehicle data from backend
        // Only set customerName if it's valid (not "Unknown Customer" or empty)
        customerName:
          userSub.customer?.name &&
          userSub.customer.name.trim() !== "" &&
          userSub.customer.name.toLowerCase() !== "unknown customer"
            ? userSub.customer.name
            : undefined,
        customerPhone: userSub.customer?.phone ?? undefined,
        vehicleMake: userSub.vehicle?.make,
        vehicleModel: userSub.vehicle?.model,
        vehiclePlateNumber: userSub.vehicle?.plateNumber ?? undefined,
      };
    },
    [subscriptionPlans],
  );

  const fetchSubscriptions = useCallback(async () => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Fetch user subscriptions (customer subscriptions)
      const userSubs = await userSubscriptionService.getAllUserSubscriptions();

      // Fetch detailed data for each subscription
      const detailedSubs = await Promise.all(
        userSubs.map((sub) =>
          userSubscriptionService.getUserSubscriptionById(sub.id),
        ),
      );

      // Transform to frontend format
      const transformed = detailedSubs.map(transformUserSubscription);
      setSubscriptions(transformed);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch subscriptions";
      setError(errorMessage);
      console.error("Error fetching subscriptions:", error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [transformUserSubscription, isAuthenticated]);

  const refreshSubscriptions = useCallback(async () => {
    await fetchSubscriptions();
  }, [fetchSubscriptions]);

  const addSubscription = useCallback(
    (subscriptionData: Omit<Subscription, "id">) => {
      const newSubscription: Subscription = {
        ...subscriptionData,
        id: `SUB${String(subscriptions.length + 1).padStart(3, "0")}`,
      };

      setSubscriptions((prev) => [newSubscription, ...prev]);

      const customer = customers.find(
        (c) => c.id === subscriptionData.customerId,
      );
      if (customer) {
        addActivity({
          userId: "SYSTEM",
          action: "activated",
          module: "subscription",
          recordId: newSubscription.id,
          description: `${subscriptionData.planName} subscription activated for ${customer.name}`,
        });
      }

      return newSubscription;
    },
    [customers, addActivity, subscriptions.length],
  );

  const createUserSubscription = useCallback(
    async (
      formData: AddSubscriptionFormData,
      planFeatures?: SubscriptionFeature[],
    ): Promise<Subscription> => {
      // Get the subscription plan to extract name and price
      const plan = subscriptionPlans.find(
        (p) => p.id === formData.subscriptionId,
      );

      // Use provided features or get from plan
      let features: SubscriptionFeature[] = [];

      if (planFeatures && planFeatures.length > 0) {
        // Use features from API subscription plan
        features = planFeatures;
      } else if (plan) {
        // Fallback: transform from frontend SubscriptionPlan format
        features = plan.features.map((f) => ({
          id: f.name.toLowerCase().replace(/\s+/g, "_"),
          type: typeof f.included === "boolean" ? "boolean" : "number",
          value:
            typeof f.included === "boolean"
              ? String(f.included)
              : typeof f.included === "string"
                ? f.included
                : "0",
        }));
      } else {
        throw new Error("Subscription plan not found");
      }

      // Validate that subscriptionId is actually an ID, not a name
      // Ensure we're using the ID from the selected plan
      const subscriptionIdToUse = formData.subscriptionId;

      // Verify it's an ID (should be a UUID or similar format, not a plan name)
      if (!subscriptionIdToUse || subscriptionIdToUse.trim() === "") {
        throw new Error("Subscription plan ID is required");
      }

      // Prepare UserSubscriptionInput
      const userSubInput: UserSubscriptionInput = {
        customerId: formData.customerId,
        subscriptionId: subscriptionIdToUse, // Explicitly use the ID
        paymentMode: formData.paymentMode,
        details: [
          {
            day: formData.recurringDay,
            time: formData.preferredTime,
            vehicleId: formData.vehicleId1 || undefined,
          },
          // Add second rule if provided (for 8-wash plans)
          ...(formData.recurringDay2 && formData.preferredTime2
            ? [{ day: formData.recurringDay2, time: formData.preferredTime2, vehicleId: formData.vehicleId2 || undefined }]
            : []),
        ],
        features,
        // formData.discount is UI-signed (+ = surcharge); the backend column is
        // inverted, so convert here as the appointment flow does.
        discount: toBackendDiscount(formData.discount || 0),
      };

      console.log(
        "Creating user subscription with subscriptionId:",
        subscriptionIdToUse,
      );

      try {
        // Create user subscription via API
        const created =
          await userSubscriptionService.createUserSubscription(userSubInput);

        // Fetch the full details
        const detailed = await userSubscriptionService.getUserSubscriptionById(
          created.id,
        );

        // Transform to frontend format
        const transformed = transformUserSubscription(detailed);

        // Update local state
        setSubscriptions((prev) => [transformed, ...prev]);

        // Add activity
        const customer = customers.find((c) => c.id === formData.customerId);
        if (customer) {
          addActivity({
            userId: "SYSTEM",
            action: "activated",
            module: "subscription",
            recordId: transformed.id,
            description: `${plan?.name || "Subscription"} subscription activated for ${customer.name}`,
          });
        }

        toast.success("Subscription created successfully");
        return transformed;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create subscription";
        console.error("Error creating subscription:", error);
        toast.error(errorMessage);
        throw error;
      }
    },
    [
      subscriptions,
      subscriptionPlans,
      customers,
      addActivity,
      transformUserSubscription,
    ],
  );

  const getActiveSubscriptionByCustomerId = useCallback(
    (customerId: string): Subscription | undefined => {
      return subscriptions.find(
        (s) => s.customerId === customerId && s.status === "active",
      );
    },
    [subscriptions],
  );

  const getActiveSubscriptionsByCustomerId = useCallback(
    (customerId: string): Subscription[] => {
      return subscriptions.filter(
        (s) =>
          s.customerId === customerId &&
          s.status === "active" &&
          s.remainingServices > 0,
      );
    },
    [subscriptions],
  );

  const getSubscriptionsByCustomerId = useCallback(
    (customerId: string): Subscription[] => {
      return subscriptions.filter((s) => s.customerId === customerId);
    },
    [subscriptions],
  );

  /**
   * Check if a customer has an active Platinum subscription
   * Used to determine if 15% discount applies to one-time service appointments
   */
  const hasActivePlatinumSubscription = useCallback(
    (customerId: string): boolean => {
      const platinumPlans = ["PLATINUM", "PLATINUM LITE"];
      return subscriptions.some(
        (s) =>
          s.customerId === customerId &&
          s.status === "active" &&
          platinumPlans.includes(s.planName?.toUpperCase() ?? ""),
      );
    },
    [subscriptions],
  );

  const updateSubscription = useCallback(
    (id: string, subscriptionData: Partial<Subscription>) => {
      setSubscriptions((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...subscriptionData } : s)),
      );
    },
    [],
  );

  const updateUserSubscriptionDetails = useCallback(
    async (
      id: string,
      updates: {
        day?: string;
        time?: string;
        vehicleId?: string;
        day2?: string;
        time2?: string;
        vehicleId2?: string;
      },
    ) => {
      try {
        // Fetch current subscription to get existing rules with IDs
        const currentSubscription =
          await userSubscriptionService.getUserSubscriptionById(id);

        if (
          !currentSubscription.rules ||
          currentSubscription.rules.length === 0
        ) {
          throw new Error("Subscription has no rules to update");
        }

        // Construct details array with rule IDs preserved, applying updates per rule index
        const updatedDetails = currentSubscription.rules.map((rule, index) => {
          if (index === 0) {
            return {
              id: rule.id,
              day: updates.day ? updates.day.toUpperCase() : rule.day.toUpperCase(),
              time: updates.time || rule.time,
              vehicleId: updates.vehicleId !== undefined ? updates.vehicleId : rule.vehicleId,
            };
          }
          if (index === 1) {
            return {
              id: rule.id,
              day: updates.day2 ? updates.day2.toUpperCase() : rule.day.toUpperCase(),
              time: updates.time2 || rule.time,
              vehicleId: updates.vehicleId2 !== undefined ? updates.vehicleId2 : rule.vehicleId,
            };
          }
          return {
            id: rule.id,
            day: rule.day,
            time: rule.time,
            vehicleId: rule.vehicleId,
          };
        });

        // If we are updating a 2-rule plan but it only had 1 rule, append the 2nd rule
        if (updates.day2 && updates.time2 && updatedDetails.length === 1) {
          updatedDetails.push({
            id: crypto.randomUUID(),
            day: updates.day2.toUpperCase(),
            time: updates.time2,
            vehicleId: updates.vehicleId2,
          });
        }

        // Prepare update input - only update details, preserve isActive, subscriptionId, and features
        // Features are preserved (not updated) - we only update details
        const updateInput: UserSubscriptionUpdateInput = {
          details: updatedDetails,
          // features: currentSubscription.features || [], // Preserve existing features (not updated)
          isActive: currentSubscription.isActive, // Preserve current status (status is updated via separate PATCH)
          subscriptionId: currentSubscription.subscriptionId,
        };

        // Update via PUT endpoint
        await userSubscriptionService.updateUserSubscription(id, updateInput);

        // Refresh subscriptions to get updated data
        await refreshSubscriptions();

        toast.success("Subscription details updated successfully");
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update subscription details";
        console.error("Error updating subscription details:", error);
        toast.error(errorMessage);
        throw error;
      }
    },
    [refreshSubscriptions],
  );

  const updateUserSubscriptionIsActive = useCallback(
    async (id: string, isActive: boolean) => {
      try {
        // Update via PATCH endpoint
        const updated = await userSubscriptionService.updateUserSubscriptionIsActive(
          id,
          isActive,
        );

        // Refresh subscriptions to get updated data
        await refreshSubscriptions();

        toast.success(
          `Subscription ${isActive ? "activated" : "expired"} successfully`,
        );

        return updated;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update subscription status";
        console.error("Error updating subscription status:", error);
        toast.error(errorMessage);
        throw error;
      }
    },
    [refreshSubscriptions],
  );

  const deleteSubscription = useCallback((id: string) => {
    setSubscriptions((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const getSubscriptionById = useCallback(
    (id: string) => {
      return subscriptions.find((s) => s.id === id);
    },
    [subscriptions],
  );

  // Fetch subscriptions on mount, but only if authenticated
  useEffect(() => {
    // Wait for auth to finish loading, then check if authenticated
    if (!authLoading && isAuthenticated) {
      fetchSubscriptions();
    }
  }, [fetchSubscriptions, authLoading, isAuthenticated]);

  const value: SubscriptionContextType = {
    subscriptions,
    loading,
    error,
    addSubscription,
    createUserSubscription,
    updateSubscription,
    updateUserSubscriptionDetails,
    updateUserSubscriptionIsActive,
    deleteSubscription,
    getSubscriptionById,
    getActiveSubscriptionByCustomerId,
    getActiveSubscriptionsByCustomerId,
    getSubscriptionsByCustomerId,
    hasActivePlatinumSubscription,
    fetchSubscriptions,
    refreshSubscriptions,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscriptions() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useSubscriptions must be used within a SubscriptionProvider",
    );
  }
  return context;
}
